/**
 * API Service for fetching data from PHP backend
 * 
 * Configure your API base URL in environment variables:
 * Create a .env file in the root directory with:
 * REACT_APP_API_BASE_URL=https://reboon.org/Admin/api
 * 
 * Or modify the default value below
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://reboon.org/Admin/api';

/**
 * Generic API fetch function
 * @param {string} endpoint - API endpoint path
 * @param {object} options - Fetch options
 * @returns {Promise} - Response data
 */
const apiFetch = async (endpoint, options = {}) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Fetch Error:', error);
    throw error;
  }
};

/**
 * Fetch carousel images from API
 * 
 * Expected API Response Format:
 * Option 1: { images: [{ id: 1, url: "http://...", alt: "Description", order: 1 }] }
 * Option 2: [{ id: 1, url: "http://...", alt: "Description" }]
 * Option 3: ["http://image1.jpg", "http://image2.jpg"] (simple array of URLs)
 * 
 * @returns {Promise<Array>} - Array of carousel image objects or URLs
 */
export const fetchCarouselImages = async () => {
  try {
    const data = await apiFetch('/carousel.php');
    // Expected format: { images: [{ id, url, alt, order }] } or array directly
    return data.images || data || [];
  } catch (error) {
    console.error('Error fetching carousel images:', error);
    return [];
  }
};

/**
 * Fetch dynamic sections from API
 * 
 * Expected API Response Format:
 * { 
 *   sections: [
 *     { 
 *       id: 1, 
 *       sectionId: "womens", 
 *       title: { EN: "Womens", KU: "خانمانی", AR: "النساء", TR: "Kadınlar" },
 *       images: [{ id: 1, url: "http://...", alt: "Description" }],
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * Or directly as an array:
 * [
 *   { id: 1, sectionId: "womens", title: {...}, images: [...], order: 0 }
 * ]
 * 
 * @returns {Promise<Array>} - Array of section objects
 */
export const fetchHomeSections = async () => {
  try {
    const data = await apiFetch('/home/sections.php');
    // Expected format: { sections: [...] } or array directly
    return data.sections || data || [];
  } catch (error) {
    console.error('Error fetching home sections:', error);
    return [];
  }
};

/**
 * Fetch activities from API
 * 
 * Expected API Response Format:
 * { 
 *   activities: [
 *     { 
 *       id: 1, 
 *       title: { EN: "Title", KU: "ناونیشان", AR: "العنوان", TR: "Başlık" },
 *       description: { EN: "Description", KU: "وەسف", AR: "وصف", TR: "Açıklama" },
 *       images: [{ id: 1, url: "http://...", alt: "Description" }],
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * @returns {Promise<Array>} - Array of activity objects
 */
export const fetchActivities = async () => {
  try {
    const data = await apiFetch('/activities/activities.php');
    // Expected format: { activities: [...] } or array directly
    return data.activities || data || [];
  } catch (error) {
    console.error('Error fetching activities:', error);
    return [];
  }
};

/**
 * Fetch publications from API
 * 
 * Expected API Response Format:
 * { 
 *   publications: [
 *     { 
 *       id: 1, 
 *       title: { EN: "Title", KU: "ناونیشان", AR: "العنوان", TR: "Başlık" },
 *       description: { EN: "Description", KU: "وەسف", AR: "وصف", TR: "Açıklama" },
 *       image: "http://...",
 *       alt: "Description",
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * @returns {Promise<Array>} - Array of publication objects
 */
export const fetchPublications = async () => {
  try {
    const data = await apiFetch('/publications/publications.php');
    // Expected format: { publications: [...] } or array directly
    return data.publications || data || [];
  } catch (error) {
    console.error('Error fetching publications:', error);
    return [];
  }
};

/**
 * Fetch Sanad carousel images from API
 * 
 * @returns {Promise<Array>} - Array of carousel image objects
 */
export const fetchSanadCarouselImages = async () => {
  try {
    const data = await apiFetch('/sanad/carousel.php');
    return data.images || data || [];
  } catch (error) {
    console.error('Error fetching sanad carousel images:', error);
    return [];
  }
};

/**
 * Fetch Sanad books from API
 * 
 * Expected API Response Format:
 * { 
 *   books: [
 *     { 
 *       id: 1, 
 *       cover: "http://...",
 *       pdf: "http://...",
 *       title: { EN: "Title", KU: "ناونیشان", AR: "العنوان", TR: "Başlık" },
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * @returns {Promise<Array>} - Array of book objects
 */
export const fetchSanadBooks = async () => {
  try {
    const data = await apiFetch('/sanad/books.php');
    return data.books || data || [];
  } catch (error) {
    console.error('Error fetching sanad books:', error);
    return [];
  }
};

/**
 * Fetch applications from API
 * 
 * Expected API Response Format:
 * { 
 *   applications: [
 *     { 
 *       id: 1, 
 *       name: { EN: "Title", KU: "ناونیشان", AR: "العنوان", TR: "Başlık" },
 *       description: { EN: "Description", KU: "وەسف", AR: "وصف", TR: "Açıklama" },
 *       image: "http://...",
 *       alt: "Description",
 *       appStoreLink: "http://...",
 *       playStoreLink: "http://...",
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * @returns {Promise<Array>} - Array of application objects
 */
export const fetchApplications = async () => {
  try {
    const data = await apiFetch('/applications/applications.php');
    return data.applications || data || [];
  } catch (error) {
    console.error('Error fetching applications:', error);
    return [];
  }
};

/**
 * Fetch podcasts from API
 * 
 * Expected API Response Format:
 * { 
 *   podcasts: [
 *     { 
 *       id: 1, 
 *       title: { EN: "Title", KU: "ناونیشان", AR: "العنوان", TR: "Başlık" },
 *       description: { EN: "Description", KU: "وەسف", AR: "وصف", TR: "Açıklama" },
 *       image: "http://...",
 *       alt: "Description",
 *       link: "http://...",
 *       linkText: { EN: "Listen Now", KU: "گوێگرتن", AR: "استمع الآن", TR: "Şimdi Dinle" },
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * @returns {Promise<Array>} - Array of podcast objects
 */
export const fetchPodcasts = async () => {
  try {
    const data = await apiFetch('/podcasts/podcasts.php');
    return data.podcasts || data || [];
  } catch (error) {
    console.error('Error fetching podcasts:', error);
    return [];
  }
};

/**
 * Fetch Academia carousel images from API
 * 
 * @returns {Promise<Array>} - Array of carousel image objects
 */
export const fetchAcademiaCarouselImages = async () => {
  try {
    const data = await apiFetch('/academia/carousel.php');
    return data.images || data || [];
  } catch (error) {
    console.error('Error fetching academia carousel images:', error);
    return [];
  }
};

/**
 * Fetch Academia items from API
 * 
 * Expected API Response Format:
 * { 
 *   items: [
 *     { 
 *       id: 1, 
 *       title: { EN: "Title", KU: "ناونیشان", AR: "العنوان", TR: "Başlık" },
 *       description: { EN: "Description", KU: "وەسف", AR: "وصف", TR: "Açıklama" },
 *       images: [{ id: 1, url: "http://...", alt: "Description" }],
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * @returns {Promise<Array>} - Array of item objects
 */
export const fetchAcademiaItems = async () => {
  try {
    const data = await apiFetch('/academia/items.php');
    return data.items || data || [];
  } catch (error) {
    console.error('Error fetching academia items:', error);
    return [];
  }
};

/**
 * Fetch Womens content from API
 * 
 * Expected API Response Format:
 * { 
 *   content: [
 *     { 
 *       id: 1,
 *       section_type: 'about' | 'activities' | 'academia' | 'publishings' | 'podcasts' | 'applications',
 *       title: { EN: "Title", KU: "ناونیشان", AR: "العنوان", TR: "Başlık" },
 *       description: { EN: "Description", KU: "وەسف", AR: "وصف", TR: "Açıklama" },
 *       content: { EN: "Content", KU: "ناوەڕۆک", AR: "المحتوى", TR: "İçerik" },
 *       image: "http://...",
 *       link: "http://...",
 *       linkText: { EN: "Link Text", KU: "دەق", AR: "نص", TR: "Bağlantı" },
 *       appStoreLink: "http://...",
 *       playStoreLink: "http://...",
 *       images: [{ id: 1, url: "http://...", alt: "Description" }],
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * @returns {Promise<Array>} - Array of content objects
 */
export const fetchWomensContent = async () => {
  try {
    const data = await apiFetch('/womens/womens.php');
    return data.content || data || [];
  } catch (error) {
    console.error('Error fetching womens content:', error);
    return [];
  }
};

/**
 * Generate or retrieve device ID from localStorage
 * @returns {string} - Unique device ID
 */
const getDeviceId = () => {
  const STORAGE_KEY = 'reboon_device_id';
  let deviceId = localStorage.getItem(STORAGE_KEY);
  
  if (!deviceId) {
    // Generate a unique device ID
    deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(STORAGE_KEY, deviceId);
  }
  
  return deviceId;
};

/**
 * Track a visitor and get visitor count
 * 
 * Expected API Response Format:
 * { 
 *   is_new: boolean,
 *   total: number,
 *   count: number
 * }
 * 
 * @returns {Promise<number>} - Total visitor count
 */
export const trackVisitor = async () => {
  try {
    const deviceId = getDeviceId();
    const data = await apiFetch('/visitors.php', {
      method: 'POST',
      body: JSON.stringify({ device_id: deviceId }),
    });
    return data.total || data.count || 0;
  } catch (error) {
    console.error('Error tracking visitor:', error);
    return 0;
  }
};

/**
 * Get visitor count without tracking
 * 
 * Expected API Response Format:
 * { 
 *   total: number,
 *   count: number
 * }
 * 
 * @returns {Promise<number>} - Total visitor count
 */
export const fetchVisitorCount = async () => {
  try {
    const data = await apiFetch('/visitors.php');
    return data.total || data.count || 0;
  } catch (error) {
    console.error('Error fetching visitor count:', error);
    return 0;
  }
};

const apiService = {
  fetchCarouselImages,
  fetchHomeSections,
  fetchActivities,
  fetchPublications,
  fetchSanadCarouselImages,
  fetchSanadBooks,
  fetchApplications,
  fetchPodcasts,
  fetchAcademiaCarouselImages,
  fetchAcademiaItems,
  fetchWomensContent,
  trackVisitor,
  fetchVisitorCount,
  apiFetch,
};

export default apiService;

